import { useEffect, useMemo, useState } from 'react'
import s from './AccompanyingNotesReport.modules.scss'
import { useNotification } from 'hooks'
import { DownloadActionIcon, ExportActionIcon } from 'icons'
import { getColumns, getFilters } from './helpers'
import {
  bulkDownload,
  downloadDocument,
  downloadReportCsv,
  getCollectingCompanyOptions,
  getLegalEntitiesOptions,
  getLocationOptions,
  getWasteCategoryOptions
} from '../api'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { Table } from 'components/core-ui/Table'
import { BulkDownloadFilters, TableState, WasteCategoryReportTableItem } from '../interfaces'
import { useImmer } from 'use-immer'
import { BulkDownloadModal } from '../common/BulkDownloadModal'

const AccompanyingNotesReport = () => {
  const [tableItems, setTableItems] = useState<WasteCategoryReportTableItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [rowDownloadId, setRowDownloadId] = useState<number>(0)
  const [documentType, setDocumentType] = useState<string>('')
  const [isBulkDownloadModalOpen, setIsBulkDownloadModalOpen] = useState<boolean>(false)
  const [bulkDownloadFilters, setBulkDownloadFilters] = useImmer<BulkDownloadFilters>({
    locations: [],
    startDate: null,
    endDate: null
  })
  const [tableState, setTableState] = useState<TableState>({
    order: null,
    orderBy: null,
    tableFilters: null
  })

  const { showNotification } = useNotification()

  const openBulkDownloadModal = (): void => setIsBulkDownloadModalOpen(true)
  const closeBulkDownloadModal = (): void => setIsBulkDownloadModalOpen(false)

  const toolbarActions = useMemo(
    () => [
      {
        label: 'Export Excel',
        icon: <ExportActionIcon />,
        onClick: () => {
          downloadReportCsvQuery.refetch()
        }
      },
      {
        label: 'Download',
        icon: <DownloadActionIcon />,
        onClick: () => openBulkDownloadModal()
      }
    ],
    []
  )

  const { data: legalEntitiesOptions } = getLegalEntitiesOptions(showNotification)

  const { data: shopOptions } = getLocationOptions(showNotification)

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const { data: companyOptions } = getCollectingCompanyOptions(showNotification)

  const selectDocumentForDownload = (rowId: number, documentType: string) => {
    setRowDownloadId(rowId)
    setDocumentType(documentType)
  }

  const columns = useMemo(() => getColumns(selectDocumentForDownload), [])

  const filters = useMemo(
    () => getFilters(legalEntitiesOptions, shopOptions, companyOptions, wasteCategoryOptions),
    [legalEntitiesOptions, shopOptions, companyOptions, wasteCategoryOptions]
  )

  const downloadQuery = downloadDocument(
    showNotification,
    'ACCOMPANY_NOTE',
    rowDownloadId,
    documentType,
    () => {
      setRowDownloadId(0)
      setDocumentType('')
    }
  )

  const downloadReportCsvQuery = downloadReportCsv(
    showNotification,
    toTableGETUrl({
      url: '/waste-management-system/report/waste-accompanying-note/csv',
      tableFilters: tableState.tableFilters
    })
  )

  const bulkDownloadQuery = bulkDownload(
    showNotification,
    bulkDownloadFilters.locations?.map(location => `${location.value}`).join(','),
    bulkDownloadFilters.startDate,
    bulkDownloadFilters.endDate,
    closeBulkDownloadModal
  )

  useEffect(() => {
    if (rowDownloadId) {
      downloadQuery.refetch()
    }
  }, [rowDownloadId, documentType])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/report/waste-accompanying-note',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableState({
        order,
        orderBy,
        tableFilters
      })

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru avizele de insotire. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  const downloadBulkDocuments = () => {
    bulkDownloadQuery.refetch()
  }

  return (
    <div className={s.mainContainer}>
      <BulkDownloadModal
        isModalOpen={isBulkDownloadModalOpen}
        closeModal={closeBulkDownloadModal}
        filters={bulkDownloadFilters}
        setFilters={setBulkDownloadFilters}
        downloadBulkDocuments={downloadBulkDocuments}
      />
      <Table
        hideCheckbox
        orderBy="creationDate"
        order="desc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        stickyRatio={0.4}
        rowActions={[
          {
            label: 'Download',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#33691E',
            desktopIcon: <DownloadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => {
              setRowDownloadId(rowId)
              setDocumentType('ACCOMPANYING_NOTE')
            },
            showTooltip: true
          }
        ]}
        rowActionsWidth="80px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
      />
    </div>
  )
}

export default AccompanyingNotesReport
